import React, { useState, useEffect } from 'react';
import {
    ArrowLeft, Edit, Share2, Download, RefreshCw, MapPin, Clock,
    Package, Truck, User, Phone, Mail, Calendar, AlertTriangle,
    CheckCircle, Map, Activity, FileText, Star, Navigation,
    Play, Pause, Flag, MessageSquare, Camera, Eye, Upload,
    Container, Settings, Thermometer, Bell, Shield, Save,
    Users, Building2, ChevronRight, ExternalLink, RotateCcw,
    Target, Zap, AlertCircle, Info, CheckSquare,
    TrendingUp, BarChart3, Hash, Timer, Compass, Radio, Plus
} from 'lucide-react';

const JobDetailsPage = () => {
    const [loading, setLoading] = useState(true);
    const [job, setJob] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');
    const [selectedLeg, setSelectedLeg] = useState(1);
    const [editMode, setEditMode] = useState(false);
    const [editingField, setEditingField] = useState(null);
    const [alerts, setAlerts] = useState([]);

    // Enhanced mock data with comprehensive tracking
    useEffect(() => {
        setTimeout(() => {
            setJob({
                id: 'JOB001',
                jobNumber: 'TT-2025-001',
                customerPO: 'PO-TATA-2025-0156',
                description: 'Electronics shipment from Mumbai to Delhi warehouse',
                type: 'import',
                status: 'in_transit',
                priority: 'high',
                overallProgress: 65,
                customer: {
                    id: 'CUST001',
                    name: 'Tata Motors Ltd',
                    contact: 'Rajesh Kumar',
                    phone: '+91-9876543210',
                    email: 'rajesh.kumar@tatamotors.com',
                    address: 'Plot 15, Industrial Area, Pune - 411026',
                    rating: 4.7
                },
                vendor: {
                    id: 'VEND001',
                    name: 'Mumbai Port Trust',
                    contact: 'Mahesh Patil',
                    phone: '+91-9876543220',
                    type: 'Port Authority'
                },
                transporter: {
                    id: 'TRANS001',
                    name: 'Blue Dart Express Ltd',
                    code: 'BDE',
                    contact: 'Priya Singh',
                    phone: '+91-9876543230',
                    rating: 4.8,
                    totalJobs: 1247,
                    onTimeDelivery: 94.2,
                    available: true
                },
                container: {
                    id: 'CONT001',
                    number: 'MSKU-123456-7',
                    type: '20ft',
                    owner: 'Maersk Line',
                    tare: 2200,
                    maxWeight: 24000,
                    status: 'In Transit',
                    location: 'Highway NH4',
                    freeTime: 72,
                    usedTime: 15,
                    detentionRate: 500,
                    trackingEnabled: true,
                    temperature: 25,
                    humidity: 65,
                    lastTempReading: '2025-06-07T10:30:00'
                },
                legs: [
                    {
                        id: 1,
                        expanded: false,
                        type: 'pickup',
                        legNumber: 'LEG-001',
                        customer: 'CUST001',
                        vendor: 'VEND001',
                        transporter: 'TRANS001',
                        vehicle: {
                            id: 'VEH001',
                            number: 'MH-01-AB-1234',
                            type: 'Tata 407',
                            capacity: '25T',
                            fuelType: 'Diesel',
                            year: 2019,
                            trackingDevice: 'GPS-001',
                            lastMaintenance: '2025-05-15'
                        },
                        driver: {
                            id: 'DRV001',
                            name: 'Rajesh Kumar',
                            phone: '+91-9876543201',
                            license: 'MH-0720110012345',
                            experience: '8 years',
                            rating: 4.3,
                            status: 'available',
                            currentLocation: 'Mumbai'
                        },
                        origin: 'Mumbai Port Trust, Gate No. 4, Mumbai - 400001',
                        destination: 'Delhi North Warehouse, Sector 15, Gurgaon - 122001',
                        originCoords: { lat: 18.9386, lng: 72.8353 },
                        destinationCoords: { lat: 28.6139, lng: 77.2090 },
                        vehicleType: 'container_truck',
                        goodsType: 'electronics',
                        container: 'CONT001',
                        distance: 1424,
                        estimatedHours: 36,
                        actualHours: 34,
                        journeyDate: '2025-06-05',
                        journeyTime: '08:00',
                        temperatureMonitoring: false,
                        temperatureRange: '-5 to 25°C',
                        gpsTracking: true,
                        photoUpdates: true,
                        geofencing: true,
                        trackingRequired: true,
                        specialInstructions: 'Handle with care - fragile electronics',
                        status: 'completed',
                        progress: 100,
                        startTime: '2025-06-05T08:00:00',
                        endTime: '2025-06-06T18:00:00',
                        milestones: [
                            { id: 1, name: 'Vehicle Assigned', status: 'completed', timestamp: '2025-06-05T07:30:00', location: 'Mumbai Port', notes: 'Driver briefed on route' },
                            { id: 2, name: 'Loading Started', status: 'completed', timestamp: '2025-06-05T08:00:00', location: 'Mumbai Port Gate 4', notes: 'Container inspection completed' },
                            { id: 3, name: 'Journey Started', status: 'completed', timestamp: '2025-06-05T10:30:00', location: 'Mumbai Port Exit', notes: 'All documents verified' },
                            { id: 4, name: 'Reached Destination', status: 'completed', timestamp: '2025-06-06T17:30:00', location: 'Delhi Warehouse', notes: 'On-time arrival' },
                            { id: 5, name: 'Unloading Completed', status: 'completed', timestamp: '2025-06-06T18:00:00', location: 'Delhi Warehouse', notes: 'No damages reported' },
                            { id: 6, name: 'Leg Closed', status: 'completed', timestamp: '2025-06-06T18:30:00', location: 'Delhi Warehouse', notes: 'POD submitted' }
                        ],
                        polylineCoords: [
                            { lat: 18.9386, lng: 72.8353 },
                            { lat: 19.0000, lng: 73.0000 },
                            { lat: 20.0000, lng: 74.0000 },
                            { lat: 22.0000, lng: 75.0000 },
                            { lat: 25.0000, lng: 76.0000 },
                            { lat: 28.6139, lng: 77.2090 }
                        ]
                    },
                    {
                        id: 2,
                        expanded: false,
                        type: 'delivery',
                        legNumber: 'LEG-002',
                        customer: 'CUST001',
                        vendor: 'VEND001',
                        transporter: 'TRANS001',
                        vehicle: {
                            id: 'VEH002',
                            number: 'DL-02-CD-5678',
                            type: 'Eicher Pro 2049',
                            capacity: '16T',
                            fuelType: 'Diesel',
                            year: 2021,
                            trackingDevice: 'GPS-002',
                            lastMaintenance: '2025-06-01'
                        },
                        driver: {
                            id: 'DRV002',
                            name: 'Amit Patil',
                            phone: '+91-9876543202',
                            license: 'DL-0720110054321',
                            experience: '5 years',
                            rating: 4.1,
                            status: 'on_trip',
                            currentLocation: 'Highway NH48'
                        },
                        origin: 'Delhi North Warehouse, Sector 15, Gurgaon - 122001',
                        destination: 'Tata Motors Plant, Aurangabad - 431005',
                        originCoords: { lat: 28.6139, lng: 77.2090 },
                        destinationCoords: { lat: 19.8762, lng: 75.3433 },
                        vehicleType: 'container_truck',
                        goodsType: 'electronics',
                        container: 'CONT001',
                        distance: 1450,
                        estimatedHours: 30,
                        actualHours: null,
                        journeyDate: '2025-06-07',
                        journeyTime: '06:00',
                        temperatureMonitoring: false,
                        temperatureRange: '-5 to 25°C',
                        gpsTracking: true,
                        photoUpdates: true,
                        geofencing: true,
                        trackingRequired: true,
                        specialInstructions: 'Final delivery to customer facility',
                        status: 'in_transit',
                        progress: 65,
                        startTime: '2025-06-07T06:00:00',
                        endTime: null,
                        estimatedArrival: '2025-06-08T12:00:00',
                        currentLocation: { lat: 23.5000, lng: 76.0000 },
                        milestones: [
                            { id: 1, name: 'Vehicle Assigned', status: 'completed', timestamp: '2025-06-07T05:30:00', location: 'Delhi Warehouse', notes: 'Vehicle inspection completed' },
                            { id: 2, name: 'Loading Started', status: 'completed', timestamp: '2025-06-07T06:00:00', location: 'Delhi Warehouse', notes: 'Container loading in progress' },
                            { id: 3, name: 'Journey Started', status: 'completed', timestamp: '2025-06-07T08:30:00', location: 'Delhi Exit', notes: 'Route clearance obtained' },
                            { id: 4, name: 'Reached Destination', status: 'in_progress', timestamp: null, location: 'Highway NH48', notes: 'ETA: 2025-06-08 12:00' },
                            { id: 5, name: 'Unloading Ongoing', status: 'pending', timestamp: null, location: null, notes: 'Awaiting arrival' },
                            { id: 6, name: 'Leg Closed', status: 'pending', timestamp: null, location: null, notes: 'Pending completion' }
                        ],
                        polylineCoords: [
                            { lat: 28.6139, lng: 77.2090 },
                            { lat: 27.0000, lng: 76.5000 },
                            { lat: 25.5000, lng: 76.0000 },
                            { lat: 23.5000, lng: 76.0000 }, // Current location
                            { lat: 22.0000, lng: 75.5000 },
                            { lat: 19.8762, lng: 75.3433 }
                        ]
                    }
                ],
                route: {
                    totalDistance: 2874,
                    estimatedDuration: 66,
                    actualDuration: 34,
                    currentLeg: 2,
                    efficiency: 92.5,
                    fuelConsumption: 280,
                    tollCharges: 1200,
                    carbonFootprint: 890
                },
                tracking: {
                    gpsEnabled: true,
                    temperatureMonitoring: false,
                    photoUpdates: true,
                    realTimeAlerts: true,
                    geofencing: true,
                    provider: 'TruckGuru',
                    lastUpdate: '2025-06-07T10:30:00',
                    updateFrequency: '5 minutes',
                    deviceStatus: 'online',
                    batteryLevel: 85,
                    signalStrength: 'strong'
                },
                timeline: {
                    pickupDate: '2025-06-05',
                    pickupTime: '08:00',
                    deliveryDate: '2025-06-08',
                    deliveryTime: '12:00',
                    actualPickup: '2025-06-05T08:00:00',
                    estimatedDelivery: '2025-06-08T12:00:00'
                },
                specialInstructions: 'Handle with care - fragile electronics. Contact customer 1 hour before delivery.',
                kpis: {
                    onTimeDeliveryScore: 95,
                    costEfficiency: 87,
                    customerSatisfaction: 4.5,
                    complianceScore: 98,
                    fuelEfficiency: 6.2,
                    carbonEmission: 890
                }
            });

            setAlerts([
                {
                    id: 1,
                    type: 'warning',
                    severity: 'medium',
                    title: 'Route Deviation Detected',
                    message: 'Vehicle has deviated from planned route due to traffic conditions',
                    timestamp: '2025-06-07T10:15:00',
                    resolved: false,
                    category: 'route',
                    legId: 2,
                    action: 'Monitor Progress'
                },
                {
                    id: 2,
                    type: 'info',
                    severity: 'low',
                    title: 'Geofence Entry',
                    message: 'Vehicle entered Maharashtra state boundary',
                    timestamp: '2025-06-07T09:45:00',
                    resolved: true,
                    category: 'geofence',
                    legId: 2,
                    action: 'No Action Required'
                },
                {
                    id: 3,
                    type: 'success',
                    severity: 'low',
                    title: 'Checkpoint Cleared',
                    message: 'Vehicle successfully passed through Indore checkpoint',
                    timestamp: '2025-06-07T08:30:00',
                    resolved: true,
                    category: 'checkpoint',
                    legId: 2,
                    action: 'Acknowledged'
                }
            ]);

            setLoading(false);
        }, 1000);
    }, []);

    // UI Components
    const Card = ({ children, className = '' }) => (
        <div className={`bg-white border border-gray-200 rounded-lg shadow-sm ${className}`}>
            {children}
        </div>
    );

    const Button = ({ children, variant = 'primary', size = 'md', className = '', loading, onClick, ...props }) => {
        const variants = {
            primary: 'bg-blue-600 text-white hover:bg-blue-700',
            secondary: 'bg-green-600 text-white hover:bg-green-700',
            outline: 'border border-gray-300 text-gray-700 hover:bg-gray-50',
            ghost: 'text-gray-600 hover:bg-gray-100',
            danger: 'bg-red-600 text-white hover:bg-red-700',
            warning: 'bg-yellow-600 text-white hover:bg-yellow-700'
        };
        const sizes = {
            sm: 'px-3 py-1.5 text-sm',
            md: 'px-4 py-2',
            lg: 'px-6 py-3 text-lg'
        };

        return (
            <button
                className={`font-medium focus:outline-none transition-colors flex items-center justify-center rounded-lg ${variants[variant]} ${sizes[size]} ${className}`}
                onClick={onClick}
                disabled={loading}
                {...props}
            >
                {loading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>}
                {children}
            </button>
        );
    };

    const Badge = ({ children, variant = 'default', size = 'md', className = '' }) => {
        const variants = {
            default: 'bg-gray-100 text-gray-800',
            success: 'bg-green-100 text-green-800',
            warning: 'bg-yellow-100 text-yellow-800',
            danger: 'bg-red-100 text-red-800',
            blue: 'bg-blue-100 text-blue-800',
            purple: 'bg-purple-100 text-purple-800',
            info: 'bg-blue-100 text-blue-800'
        };
        const sizes = {
            sm: 'px-2 py-1 text-xs',
            md: 'px-2.5 py-0.5 text-sm'
        };

        return (
            <span className={`inline-flex items-center rounded-full font-medium ${variants[variant]} ${sizes[size]} ${className}`}>
                {children}
            </span>
        );
    };

    // Enhanced Google Maps Component with Truck Icons and Click Details
    const GoogleMap = ({ legs, currentLeg, height = "400px", showAllLegs = true }) => {
        const [mapError, setMapError] = useState(false);
        const [selectedMarker, setSelectedMarker] = useState(null);

        // Custom truck icon SVG
        // Custom truck icon SVG - replace the createTruckIcon function
        const createTruckIcon = (color = '#3B82F6', size = 40) => {
            return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
        <svg width="${size}" height="${size}" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <defs>
                <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
                    <dropShadow dx="1" dy="1" stdDeviation="1" floodColor="rgba(0,0,0,0.3)"/>
                </filter>
            </defs>
            <!-- Truck body -->
            <rect x="2" y="11" width="12" height="6" rx="1" fill="${color}" stroke="white" stroke-width="0.5" filter="url(#shadow)"/>
            <!-- Truck cab -->
            <rect x="14" y="9" width="6" height="8" rx="1" fill="${color}" stroke="white" stroke-width="0.5" filter="url(#shadow)"/>
            <!-- Wheels -->
            <circle cx="6" cy="19" r="2" fill="#374151" stroke="white" stroke-width="0.5"/>
            <circle cx="16" cy="19" r="2" fill="#374151" stroke="white" stroke-width="0.5"/>
            <!-- Wheel centers -->
            <circle cx="6" cy="19" r="0.8" fill="#6B7280"/>
            <circle cx="16" cy="19" r="0.8" fill="#6B7280"/>
            <!-- Windshield -->
            <rect x="15" y="10" width="4" height="3" rx="0.5" fill="#E5E7EB" stroke="#9CA3AF" stroke-width="0.3"/>
            <!-- Direction arrow -->
            <polygon points="20.5,13 22,13.5 20.5,14" fill="white" stroke="${color}" stroke-width="0.3"/>
        </svg>
    `);
        };

        // Info window content generator
        const createInfoWindowContent = (leg, type, legIndex) => {
            const statusColor = leg.status === 'completed' ? '#10B981' :
                leg.status === 'in_transit' ? '#3B82F6' : '#6B7280';

            return `
            <div style="max-width: 300px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
                <div style="border-bottom: 2px solid ${statusColor}; padding-bottom: 8px; margin-bottom: 12px;">
                    <h3 style="margin: 0; font-size: 16px; font-weight: 600; color: #1F2937;">
                        ${type === 'vehicle' ? '🚛 Live Vehicle Position' : `📍 Leg ${legIndex + 1} ${type === 'origin' ? 'Start' : 'End'}`}
                    </h3>
                    <div style="display: flex; align-items: center; margin-top: 4px;">
                        <span style="display: inline-block; width: 8px; height: 8px; background: ${statusColor}; border-radius: 50%; margin-right: 6px;"></span>
                        <span style="font-size: 12px; color: #6B7280; text-transform: capitalize;">${leg.status.replace('_', ' ')}</span>
                    </div>
                </div>
                
                ${type === 'vehicle' ? `
                    <div style="margin-bottom: 12px;">
                        <div style="background: #F3F4F6; padding: 8px; border-radius: 6px; margin-bottom: 8px;">
                            <div style="font-size: 14px; font-weight: 500; color: #374151;">🚛 ${leg.vehicle.number}</div>
                            <div style="font-size: 12px; color: #6B7280;">${leg.vehicle.type} • ${leg.vehicle.capacity}</div>
                        </div>
                        <div style="background: #F3F4F6; padding: 8px; border-radius: 6px;">
                            <div style="font-size: 14px; font-weight: 500; color: #374151;">👤 ${leg.driver.name}</div>
                            <div style="font-size: 12px; color: #6B7280;">📞 ${leg.driver.phone}</div>
                            <div style="font-size: 12px; color: #6B7280;">⭐ ${leg.driver.rating} rating • ${leg.driver.experience}</div>
                        </div>
                    </div>
                    
                    <div style="margin-bottom: 12px;">
                        <div style="font-size: 13px; color: #374151; margin-bottom: 4px;">
                            <strong>Progress:</strong> ${leg.progress}% complete
                        </div>
                        <div style="background: #E5E7EB; height: 6px; border-radius: 3px; overflow: hidden;">
                            <div style="background: ${statusColor}; height: 100%; width: ${leg.progress}%; transition: width 0.3s;"></div>
                        </div>
                    </div>
                    
                    <div style="margin-bottom: 12px;">
                        <div style="font-size: 13px; color: #374151; margin-bottom: 4px;"><strong>Route:</strong></div>
                        <div style="font-size: 12px; color: #6B7280; line-height: 1.4;">
                            📍 From: ${leg.origin.split(',')[0]}<br/>
                            🎯 To: ${leg.destination.split(',')[0]}<br/>
                            📏 Distance: ${leg.distance}km • ⏱️ ${leg.estimatedHours}h estimated
                        </div>
                    </div>
                ` : `
                    <div style="margin-bottom: 12px;">
                        <div style="font-size: 13px; color: #374151; margin-bottom: 4px;">
                            <strong>${type === 'origin' ? 'Origin' : 'Destination'}:</strong>
                        </div>
                        <div style="font-size: 12px; color: #6B7280; line-height: 1.4;">
                            ${type === 'origin' ? leg.origin : leg.destination}
                        </div>
                    </div>
                    
                    <div style="margin-bottom: 12px;">
                        <div style="font-size: 13px; color: #374151; margin-bottom: 4px;"><strong>Leg Details:</strong></div>
                        <div style="font-size: 12px; color: #6B7280; line-height: 1.4;">
                            🚛 Vehicle: ${leg.vehicle.number}<br/>
                            👤 Driver: ${leg.driver.name}<br/>
                            📏 ${leg.distance}km • ⏱️ ${leg.estimatedHours}h
                        </div>
                    </div>
                `}
                
                <div style="display: flex; gap: 8px; margin-top: 12px;">
                    <button onclick="window.trackVehicle('${leg.id}')" 
                            style="flex: 1; background: ${statusColor}; color: white; border: none; padding: 6px 12px; border-radius: 4px; font-size: 12px; cursor: pointer;">
                        📱 Track Live
                    </button>
                    <button onclick="window.contactDriver('${leg.driver.phone}')" 
                            style="flex: 1; background: #10B981; color: white; border: none; padding: 6px 12px; border-radius: 4px; font-size: 12px; cursor: pointer;">
                        📞 Call Driver
                    </button>
                </div>
                
                ${leg.specialInstructions ? `
                    <div style="margin-top: 12px; padding: 8px; background: #FEF3C7; border-left: 3px solid #F59E0B; border-radius: 4px;">
                        <div style="font-size: 11px; font-weight: 500; color: #92400E; margin-bottom: 2px;">⚠️ Special Instructions:</div>
                        <div style="font-size: 11px; color: #92400E;">${leg.specialInstructions}</div>
                    </div>
                ` : ''}
            </div>
        `;
        };

        useEffect(() => {
            // Global functions for info window buttons
            window.trackVehicle = (legId) => {
                console.log('Track vehicle for leg:', legId);
                // Add your tracking logic here
                alert(`Starting live tracking for leg ${legId}`);
            };

            window.contactDriver = (phone) => {
                console.log('Contacting driver:', phone);
                window.open(`tel:${phone}`);
            };

            const initMap = () => {
                try {
                    // Create map instance
                    const map = new window.google.maps.Map(document.getElementById(`map-${showAllLegs ? 'main' : currentLeg}`), {
                        zoom: 6,
                        center: { lat: 23.0225, lng: 72.5714 }, // Center of India
                        mapTypeId: 'roadmap',
                        styles: [
                            {
                                featureType: 'poi',
                                elementType: 'labels',
                                stylers: [{ visibility: 'off' }]
                            }
                        ]
                    });

                    const bounds = new window.google.maps.LatLngBounds();
                    const infoWindow = new window.google.maps.InfoWindow();

                    if (showAllLegs) {
                        // Show all legs with different colors
                        legs.forEach((leg, index) => {
                            const legColor = leg.status === 'completed' ? '#10B981' :
                                leg.status === 'in_transit' ? '#3B82F6' : '#6B7280';

                            // Create polyline for each leg
                            if (leg.polylineCoords) {
                                const polyline = new window.google.maps.Polyline({
                                    path: leg.polylineCoords,
                                    geodesic: true,
                                    strokeColor: legColor,
                                    strokeOpacity: 1.0,
                                    strokeWeight: leg.status === 'in_transit' ? 5 : 3
                                });
                                polyline.setMap(map);

                                // Add waypoints to bounds
                                leg.polylineCoords.forEach(coord => {
                                    bounds.extend(coord);
                                });
                            }

                            // Origin marker with truck icon
                            const originMarker = new window.google.maps.Marker({
                                position: leg.originCoords,
                                map: map,
                                title: `Leg ${index + 1} Start: ${leg.origin}`,
                                icon: {
                                    url: 'https://maps.google.com/mapfiles/ms/icons/green-dot.png',
                                    scaledSize: new window.google.maps.Size(32, 32)
                                }
                            });

                            // Add click listener for origin
                            originMarker.addListener('click', () => {
                                infoWindow.setContent(createInfoWindowContent(leg, 'origin', index));
                                infoWindow.open(map, originMarker);
                                setSelectedMarker(`origin-${leg.id}`);
                            });

                            // Destination marker with truck icon
                            const destMarker = new window.google.maps.Marker({
                                position: leg.destinationCoords,
                                map: map,
                                title: `Leg ${index + 1} End: ${leg.destination}`,
                                icon: {
                                    url: index === legs.length - 1 ?
                                        'https://maps.google.com/mapfiles/ms/icons/red-dot.png' :
                                        'https://maps.google.com/mapfiles/ms/icons/yellow-dot.png',
                                    scaledSize: new window.google.maps.Size(32, 32)
                                }
                            });

                            // Add click listener for destination
                            destMarker.addListener('click', () => {
                                infoWindow.setContent(createInfoWindowContent(leg, 'destination', index));
                                infoWindow.open(map, destMarker);
                                setSelectedMarker(`destination-${leg.id}`);
                            });

                            // Current vehicle position for active leg with animated truck
                            if (leg.status === 'in_transit' && leg.currentLocation) {
                                const truckIconSvg = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                                    <svg width="48" height="48" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <defs>
                                            <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
                                                <dropShadow dx="2" dy="2" stdDeviation="2" floodColor="rgba(0,0,0,0.4)"/>
                                            </filter>
                                        </defs>
                                        <!-- Background circle -->
                                        <circle cx="12" cy="12" r="11" fill="#3B82F6" stroke="white" stroke-width="2" filter="url(#shadow)"/>
                                        <!-- Truck icon -->
                                        <g transform="translate(6, 6) scale(0.5)">
                                            <!-- Truck body -->
                                            <rect x="2" y="8" width="14" height="8" rx="1" fill="white"/>
                                            <!-- Truck cab -->
                                            <rect x="16" y="6" width="6" height="10" rx="1" fill="white"/>
                                            <!-- Wheels -->
                                            <circle cx="6" cy="18" r="2" fill="#374151"/>
                                            <circle cx="18" cy="18" r="2" fill="#374151"/>
                                            <!-- Windshield -->
                                            <rect x="17" y="7" width="4" height="4" rx="0.5" fill="#E5E7EB"/>
                                        </g>
                                        <!-- Pulse animation ring -->
                                        <circle cx="12" cy="12" r="11" fill="none" stroke="#3B82F6" stroke-width="2" opacity="0.6">
                                            <animate attributeName="r" values="11;16;11" dur="2s" repeatCount="indefinite"/>
                                            <animate attributeName="opacity" values="0.6;0;0.6" dur="2s" repeatCount="indefinite"/>
                                        </circle>
                                    </svg>
                                `);

                                const vehicleMarker = new window.google.maps.Marker({
                                    position: leg.currentLocation,
                                    map: map,
                                    title: `${leg.vehicle.number} (Live Position)`,
                                    icon: {
                                        url: truckIconSvg,
                                        scaledSize: new window.google.maps.Size(48, 48),
                                        anchor: new window.google.maps.Point(24, 24)
                                    },
                                    animation: window.google.maps.Animation.BOUNCE
                                });
                            }

                            bounds.extend(leg.originCoords);
                            bounds.extend(leg.destinationCoords);
                        });
                    } else {
                        // Show single leg details
                        const leg = legs[currentLeg - 1];
                        if (leg) {
                            // Origin marker with truck icon
                            const originMarker = new window.google.maps.Marker({
                                position: leg.originCoords,
                                map: map,
                                title: leg.origin,
                                icon: {
                                    url: 'https://maps.google.com/mapfiles/ms/icons/green-dot.png',
                                    scaledSize: new window.google.maps.Size(40, 40)
                                }
                            });

                            originMarker.addListener('click', () => {
                                infoWindow.setContent(createInfoWindowContent(leg, 'origin', currentLeg - 1));
                                infoWindow.open(map, originMarker);
                            });

                            // Destination marker with truck icon
                            const destMarker = new window.google.maps.Marker({
                                position: leg.destinationCoords,
                                map: map,
                                title: leg.destination,
                                icon: {
                                    url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
                                    scaledSize: new window.google.maps.Size(40, 40)
                                }
                            });

                            destMarker.addListener('click', () => {
                                infoWindow.setContent(createInfoWindowContent(leg, 'destination', currentLeg - 1));
                                infoWindow.open(map, destMarker);
                            });

                            // Polyline for the route
                            if (leg.polylineCoords) {
                                const polyline = new window.google.maps.Polyline({
                                    path: leg.polylineCoords,
                                    geodesic: true,
                                    strokeColor: '#3B82F6',
                                    strokeOpacity: 1.0,
                                    strokeWeight: 5
                                });
                                polyline.setMap(map);
                            }

                            // Current vehicle position with truck icon
                            if (leg.status === 'in_transit' && leg.currentLocation) {
                                const truckIconSvg = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                                    <svg width="50" height="50" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <defs>
                                            <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
                                                <dropShadow dx="2" dy="2" stdDeviation="2" floodColor="rgba(0,0,0,0.4)"/>
                                            </filter>
                                        </defs>
                                        <!-- Background circle -->
                                        <circle cx="12" cy="12" r="11" fill="#10B981" stroke="white" stroke-width="2" filter="url(#shadow)"/>
                                        <!-- Truck icon -->
                                        <g transform="translate(6, 6) scale(0.5)">
                                            <!-- Truck body -->
                                            <rect x="2" y="8" width="14" height="8" rx="1" fill="white"/>
                                            <!-- Truck cab -->
                                            <rect x="16" y="6" width="6" height="10" rx="1" fill="white"/>
                                            <!-- Wheels -->
                                            <circle cx="6" cy="18" r="2" fill="#374151"/>
                                            <circle cx="18" cy="18" r="2" fill="#374151"/>
                                            <!-- Windshield -->
                                            <rect x="17" y="7" width="4" height="4" rx="0.5" fill="#E5E7EB"/>
                                        </g>
                                        <!-- Double pulse animation -->
                                        <circle cx="12" cy="12" r="11" fill="none" stroke="#10B981" stroke-width="2" opacity="0.8">
                                            <animate attributeName="r" values="11;18;11" dur="1.5s" repeatCount="indefinite"/>
                                            <animate attributeName="opacity" values="0.8;0;0.8" dur="1.5s" repeatCount="indefinite"/>
                                        </circle>
                                    </svg>
                                `);

                                const vehicleMarker = new window.google.maps.Marker({
                                    position: leg.currentLocation,
                                    map: map,
                                    title: `${leg.vehicle.number} (Live)`,
                                    icon: {
                                        url: truckIconSvg,
                                        scaledSize: new window.google.maps.Size(50, 50),
                                        anchor: new window.google.maps.Point(25, 25)
                                    }
                                });
                            }

                            bounds.extend(leg.originCoords);
                            bounds.extend(leg.destinationCoords);
                        }
                    }

                    // Fit map to show all markers
                    if (!bounds.isEmpty()) {
                        map.fitBounds(bounds);
                    }
                } catch (error) {
                    console.error('Error initializing map:', error);
                    setMapError(true);
                }
            };

            // Load Google Maps API if not already loaded
            if (window.google?.maps) {
                initMap();
            } else {
                const script = document.createElement('script');
                script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyDEtXDWbePaNDXrNFiNyGJ1DmwyrXxuVRg&libraries=geometry`;
                script.onload = initMap;
                script.onerror = () => setMapError(true);
                document.head.appendChild(script);
            }
        }, [legs, currentLeg, showAllLegs]);

        return (
            <div
                id={`map-${showAllLegs ? 'main' : currentLeg}`}
                style={{ height, width: '100%', borderRadius: '8px' }}
                className="border border-gray-200"
            />
        );
    };

    // Horizontal Tracker Component
    const HorizontalTracker = ({ milestones, legNumber }) => {
        const getStatusColor = (status) => {
            switch (status) {
                case 'completed': return 'bg-green-500 text-white';
                case 'in_progress': return 'bg-blue-500 text-white animate-pulse';
                case 'pending': return 'bg-gray-300 text-gray-600';
                default: return 'bg-gray-300 text-gray-600';
            }
        };

        const getStatusIcon = (status, index) => {
            switch (status) {
                case 'completed': return <CheckCircle className="w-4 h-4" />;
                case 'in_progress': return <Radio className="w-4 h-4" />;
                case 'pending': return <span className="text-xs font-bold">{index + 1}</span>;
                default: return <span className="text-xs font-bold">{index + 1}</span>;
            }
        };

        return (
            <div className="p-6 bg-gradient-to-r from-blue-50 to-green-50 rounded-lg">
                <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold text-gray-900">Leg {legNumber} Progress Tracker</h4>
                    <Badge variant="blue" size="sm">Live Updates</Badge>
                </div>

                <div className="relative">
                    {/* Progress Line */}
                    <div className="absolute top-6 left-6 right-6 h-1 bg-gray-200 rounded-full">
                        <div
                            className="h-full bg-gradient-to-r from-blue-500 to-green-500 rounded-full transition-all duration-1000"
                            style={{
                                width: `${(milestones.filter(m => m.status === 'completed').length / milestones.length) * 100}%`
                            }}
                        />
                    </div>

                    {/* Milestones */}
                    <div className="flex justify-between relative">
                        {milestones.map((milestone, index) => (
                            <div key={milestone.id} className="flex flex-col items-center">
                                {/* Icon */}
                                <div className={`w-12 h-12 rounded-full flex items-center justify-center border-4 border-white shadow-lg ${getStatusColor(milestone.status)}`}>
                                    {getStatusIcon(milestone.status, index)}
                                </div>

                                {/* Label */}
                                <div className="mt-3 text-center max-w-20">
                                    <p className="text-xs font-medium text-gray-900 leading-tight">{milestone.name}</p>
                                    {milestone.timestamp && (
                                        <p className="text-xs text-gray-500 mt-1">
                                            {new Date(milestone.timestamp).toLocaleTimeString([], {
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            })}
                                        </p>
                                    )}
                                    {milestone.location && (
                                        <p className="text-xs text-blue-600 mt-1 truncate">{milestone.location}</p>
                                    )}
                                </div>

                                {/* Status Indicator */}
                                {milestone.status === 'in_progress' && (
                                    <div className="absolute -bottom-2 w-2 h-2 bg-blue-500 rounded-full animate-ping"></div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Current Status Details */}
                {milestones.find(m => m.status === 'in_progress') && (
                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <Activity className="w-4 h-4 text-blue-600 mr-2" />
                                <span className="text-sm font-medium text-blue-900">
                                    Current: {milestones.find(m => m.status === 'in_progress')?.name}
                                </span>
                            </div>
                            <Badge variant="blue" size="sm">Live</Badge>
                        </div>
                        {milestones.find(m => m.status === 'in_progress')?.notes && (
                            <p className="text-sm text-blue-700 mt-1">
                                {milestones.find(m => m.status === 'in_progress')?.notes}
                            </p>
                        )}
                    </div>
                )}
            </div>
        );
    };

    // Editable Field Component
    const EditableField = ({ label, value, field, type = 'text', options = null, onSave }) => {
        const [isEditing, setIsEditing] = useState(false);
        const [editValue, setEditValue] = useState(value);

        const handleSave = () => {
            onSave(field, editValue);
            setIsEditing(false);
        };

        const handleCancel = () => {
            setEditValue(value);
            setIsEditing(false);
        };

        if (isEditing) {
            return (
                <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">{label}</label>
                    <div className="flex items-center space-x-2">
                        {type === 'select' ? (
                            <select
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            >
                                {options?.map(option => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                        ) : (
                            <input
                                type={type}
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                autoFocus
                            />
                        )}
                        <Button variant="primary" size="sm" onClick={handleSave}>
                            <Save className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={handleCancel}>
                            <RotateCcw className="w-4 h-4" />
                        </Button>
                    </div>
                </div>
            );
        }

        return (
            <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700">{label}</label>
                <div className="flex items-center justify-between group">
                    <span className="text-gray-900">{value}</span>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setIsEditing(true)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                        <Edit className="w-4 h-4" />
                    </Button>
                </div>
            </div>
        );
    };

    // Alert Component
    const AlertCard = ({ alert, onResolve, onDismiss }) => {
        const getAlertConfig = (type) => {
            switch (type) {
                case 'warning': return { bg: 'bg-yellow-50', border: 'border-yellow-200', icon: AlertTriangle, iconColor: 'text-yellow-600' };
                case 'danger': return { bg: 'bg-red-50', border: 'border-red-200', icon: AlertCircle, iconColor: 'text-red-600' };
                case 'info': return { bg: 'bg-blue-50', border: 'border-blue-200', icon: Info, iconColor: 'text-blue-600' };
                case 'success': return { bg: 'bg-green-50', border: 'border-green-200', icon: CheckCircle, iconColor: 'text-green-600' };
                default: return { bg: 'bg-gray-50', border: 'border-gray-200', icon: Info, iconColor: 'text-gray-600' };
            }
        };

        const config = getAlertConfig(alert.type);
        const AlertIcon = config.icon;

        return (
            <div className={`p-4 rounded-lg border ${config.bg} ${config.border}`}>
                <div className="flex items-start justify-between">
                    <div className="flex items-start">
                        <AlertIcon className={`w-5 h-5 mr-3 mt-0.5 ${config.iconColor}`} />
                        <div className="flex-1">
                            <h4 className="font-medium text-gray-900">{alert.title}</h4>
                            <p className="text-sm text-gray-700 mt-1">{alert.message}</p>
                            <div className="flex items-center mt-2 space-x-4">
                                <Badge variant={alert.severity === 'high' ? 'danger' : alert.severity === 'medium' ? 'warning' : 'info'} size="sm">
                                    {alert.severity.toUpperCase()}
                                </Badge>
                                <span className="text-xs text-gray-500">
                                    {new Date(alert.timestamp).toLocaleString()}
                                </span>
                                <span className="text-xs text-gray-500">
                                    Leg {alert.legId} • {alert.category}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center space-x-2 ml-4">
                        {!alert.resolved && (
                            <Button variant="outline" size="sm" onClick={() => onResolve(alert.id)}>
                                <CheckSquare className="w-4 h-4 mr-1" />
                                Resolve
                            </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => onDismiss(alert.id)}>
                            ✕
                        </Button>
                    </div>
                </div>
            </div>
        );
    };

    // Main component functions
    const handleFieldSave = (field, value) => {
        // Here you would typically make an API call to save the changes
        console.log(`Saving ${field}: ${value}`);
        // Update local state
        setJob(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleLegFieldSave = (legId, field, value) => {
        console.log(`Saving leg ${legId} ${field}: ${value}`);
        setJob(prev => ({
            ...prev,
            legs: prev.legs.map(leg =>
                leg.id === legId ? { ...leg, [field]: value } : leg
            )
        }));
    };

    const handleAlertResolve = (alertId) => {
        setAlerts(prev => prev.map(alert =>
            alert.id === alertId ? { ...alert, resolved: true } : alert
        ));
    };

    const handleAlertDismiss = (alertId) => {
        setAlerts(prev => prev.filter(alert => alert.id !== alertId));
    };

    const tabs = [
        { id: 'overview', label: 'Overview', icon: Eye },
        { id: 'tracking', label: 'Live Tracking', icon: MapPin },
        { id: 'legs', label: 'Legs Management', icon: Map },
        { id: 'timeline', label: 'Progress Timeline', icon: Activity },
        { id: 'alerts', label: 'Alerts & Monitoring', icon: Bell },
        { id: 'documents', label: 'Documents', icon: FileText }
    ];

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (!job) {
        return (
            <div className="p-6 text-center">
                <h2 className="text-2xl font-bold text-gray-900">Job Not Found</h2>
                <p className="text-gray-600 mt-2">The requested job could not be found.</p>
                <Button variant="primary" onClick={() => window.history.back()} className="mt-4">
                    Back to Jobs
                </Button>
            </div>
        );
    }

    const currentLeg = job.legs[job.route.currentLeg - 1];

    // Tab Content Components
    const OverviewTab = () => (
        <div className="space-y-6">
            {/* Key Metrics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="p-4">
                    <div className="flex items-center">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                            <TrendingUp className="w-5 h-5 text-blue-600" />
                        </div>
                        <div className="ml-3">
                            <p className="text-xs text-gray-600">Overall Progress</p>
                            <p className="text-lg font-bold text-gray-900">{job.overallProgress}%</p>
                        </div>
                    </div>
                </Card>
                <Card className="p-4">
                    <div className="flex items-center">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                            <Map className="w-5 h-5 text-green-600" />
                        </div>
                        <div className="ml-3">
                            <p className="text-xs text-gray-600">Distance</p>
                            <p className="text-lg font-bold text-gray-900">{job.route.totalDistance}km</p>
                        </div>
                    </div>
                </Card>
                <Card className="p-4">
                    <div className="flex items-center">
                        <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                            <Timer className="w-5 h-5 text-yellow-600" />
                        </div>
                        <div className="ml-3">
                            <p className="text-xs text-gray-600">Time Remaining</p>
                            <p className="text-lg font-bold text-gray-900">{job.route.estimatedDuration - (job.route.actualDuration || 0)}h</p>
                        </div>
                    </div>
                </Card>
                <Card className="p-4">
                    <div className="flex items-center">
                        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                            <Star className="w-5 h-5 text-purple-600" />
                        </div>
                        <div className="ml-3">
                            <p className="text-xs text-gray-600">Efficiency</p>
                            <p className="text-lg font-bold text-gray-900">{job.route.efficiency}%</p>
                        </div>
                    </div>
                </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Job Information */}
                <div className="lg:col-span-2 space-y-6">
                    <Card className="p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-900">Job Information</h3>
                            <Button variant="outline" size="sm" onClick={() => setEditMode(!editMode)}>
                                <Edit className="w-4 h-4 mr-2" />
                                {editMode ? 'Done Editing' : 'Edit Details'}
                            </Button>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <EditableField
                                label="Job Number"
                                value={job.jobNumber}
                                field="jobNumber"
                                onSave={handleFieldSave}
                            />
                            <EditableField
                                label="Customer PO"
                                value={job.customerPO}
                                field="customerPO"
                                onSave={handleFieldSave}
                            />
                            <EditableField
                                label="Priority"
                                value={job.priority}
                                field="priority"
                                type="select"
                                options={[
                                    { value: 'low', label: 'Low Priority' },
                                    { value: 'medium', label: 'Medium Priority' },
                                    { value: 'high', label: 'High Priority' },
                                    { value: 'urgent', label: 'Urgent' }
                                ]}
                                onSave={handleFieldSave}
                            />
                            <EditableField
                                label="Status"
                                value={job.status}
                                field="status"
                                type="select"
                                options={[
                                    { value: 'created', label: 'Created' },
                                    { value: 'assigned', label: 'Assigned' },
                                    { value: 'loading', label: 'Loading' },
                                    { value: 'in_transit', label: 'In Transit' },
                                    { value: 'delayed', label: 'Delayed' },
                                    { value: 'completed', label: 'Completed' }
                                ]}
                                onSave={handleFieldSave}
                            />
                        </div>
                        <div className="mt-4">
                            <EditableField
                                label="Description"
                                value={job.description}
                                field="description"
                                onSave={handleFieldSave}
                            />
                        </div>
                    </Card>

                    {/* Container Information */}
                    <Card className="p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                            <Container className="w-5 h-5 mr-2" />
                            Container Information
                        </h3>
                        <div className="grid grid-cols-2 gap-4">
                            <EditableField
                                label="Container Number"
                                value={job.container.number}
                                field="number"
                                onSave={(field, value) => handleFieldSave('container.number', value)}
                            />
                            <EditableField
                                label="Container Type"
                                value={job.container.type}
                                field="type"
                                type="select"
                                options={[
                                    { value: '20ft', label: '20ft Standard' },
                                    { value: '40ft', label: '40ft Standard' },
                                    { value: '40hq', label: '40ft High Cube' },
                                    { value: 'tanker', label: 'Tanker' }
                                ]}
                                onSave={(field, value) => handleFieldSave('container.type', value)}
                            />
                            <EditableField
                                label="Owner"
                                value={job.container.owner}
                                field="owner"
                                onSave={(field, value) => handleFieldSave('container.owner', value)}
                            />
                            <EditableField
                                label="Current Status"
                                value={job.container.status}
                                field="status"
                                type="select"
                                options={[
                                    { value: 'Available', label: 'Available' },
                                    { value: 'In Transit', label: 'In Transit' },
                                    { value: 'Loading', label: 'Loading' },
                                    { value: 'Unloading', label: 'Unloading' },
                                    { value: 'Maintenance', label: 'Maintenance' }
                                ]}
                                onSave={(field, value) => handleFieldSave('container.status', value)}
                            />
                        </div>

                        {/* Environmental Monitoring */}
                        {job.tracking.temperatureMonitoring && (
                            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center">
                                        <Thermometer className="w-4 h-4 text-blue-600 mr-2" />
                                        <span className="text-sm font-medium text-blue-900">Environmental Monitoring</span>
                                    </div>
                                    <div className="flex items-center space-x-4">
                                        <span className="text-sm text-blue-700">Temp: {job.container.temperature}°C</span>
                                        <span className="text-sm text-blue-700">Humidity: {job.container.humidity}%</span>
                                        <Badge variant="success" size="sm">Normal</Badge>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Detention Tracking */}
                        <div className="mt-4 p-3 bg-yellow-50 rounded-lg">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                    <Clock className="w-4 h-4 text-yellow-600 mr-2" />
                                    <span className="text-sm font-medium text-yellow-900">Detention Tracking</span>
                                </div>
                                <div className="flex items-center space-x-4">
                                    <span className="text-sm text-yellow-700">Used: {job.container.usedTime}h</span>
                                    <span className="text-sm text-yellow-700">Free: {job.container.freeTime}h</span>
                                    <Badge variant={job.container.usedTime > job.container.freeTime * 0.8 ? 'warning' : 'success'} size="sm">
                                        {job.container.usedTime > job.container.freeTime * 0.8 ? 'Risk' : 'Safe'}
                                    </Badge>
                                </div>
                            </div>
                            <div className="mt-2">
                                <div className="w-full bg-yellow-200 rounded-full h-2">
                                    <div
                                        className={`h-2 rounded-full transition-all duration-300 ${job.container.usedTime > job.container.freeTime ? 'bg-red-500' :
                                            job.container.usedTime > job.container.freeTime * 0.8 ? 'bg-yellow-500' : 'bg-green-500'
                                            }`}
                                        style={{ width: `${Math.min((job.container.usedTime / job.container.freeTime) * 100, 100)}%` }}
                                    />
                                </div>
                            </div>
                        </div>
                    </Card>

                    {/* Special Instructions */}
                    <Card className="p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                            <AlertTriangle className="w-5 h-5 mr-2 text-yellow-600" />
                            Special Instructions
                        </h3>
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                            <EditableField
                                label=""
                                value={job.specialInstructions}
                                field="specialInstructions"
                                onSave={handleFieldSave}
                            />
                        </div>
                    </Card>
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                    {/* Customer Information */}
                    <Card className="p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                            <Building2 className="w-5 h-5 mr-2" />
                            Customer Details
                        </h3>
                        <div className="space-y-3">
                            <div>
                                <h4 className="font-medium text-gray-900">{job.customer.name}</h4>
                                <p className="text-sm text-gray-600">{job.customer.contact}</p>
                            </div>
                            <div className="space-y-2">
                                <div className="flex items-center text-sm">
                                    <Phone className="w-4 h-4 text-gray-400 mr-2" />
                                    <span className="text-gray-600">{job.customer.phone}</span>
                                </div>
                                <div className="flex items-center text-sm">
                                    <Mail className="w-4 h-4 text-gray-400 mr-2" />
                                    <span className="text-gray-600">{job.customer.email}</span>
                                </div>
                                <div className="flex items-center text-sm">
                                    <Star className="w-4 h-4 text-yellow-400 mr-2" />
                                    <span className="text-gray-600">Rating: {job.customer.rating}</span>
                                </div>
                            </div>
                            <div className="flex gap-2 pt-2">
                                <Button variant="outline" size="sm" className="flex-1">
                                    <Phone className="w-4 h-4 mr-1" />
                                    Call
                                </Button>
                                <Button variant="outline" size="sm" className="flex-1">
                                    <Mail className="w-4 h-4 mr-1" />
                                    Email
                                </Button>
                            </div>
                        </div>
                    </Card>

                    {/* Transporter Information */}
                    <Card className="p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                                <Truck className="w-5 h-5 mr-2" />
                                Transporter
                            </h3>
                            <Button variant="outline" size="sm">
                                <Edit className="w-4 h-4 mr-1" />
                                Change
                            </Button>
                        </div>
                        <div className="space-y-3">
                            <div>
                                <h4 className="font-medium text-gray-900">{job.transporter.name}</h4>
                                <p className="text-sm text-gray-600">Code: {job.transporter.code}</p>
                            </div>
                            <div className="space-y-2">
                                <div className="flex items-center text-sm">
                                    <User className="w-4 h-4 text-gray-400 mr-2" />
                                    <span className="text-gray-600">{job.transporter.contact}</span>
                                </div>
                                <div className="flex items-center text-sm">
                                    <Star className="w-4 h-4 text-yellow-400 mr-2" />
                                    <span className="text-gray-600">Rating: {job.transporter.rating}</span>
                                </div>
                                <div className="text-sm text-gray-600">
                                    <p>{job.transporter.totalJobs} total jobs</p>
                                    <p>{job.transporter.onTimeDelivery}% on-time delivery</p>
                                </div>
                                <div className="flex items-center">
                                    <div className={`w-2 h-2 rounded-full mr-2 ${job.transporter.available ? 'bg-green-500' : 'bg-red-500'}`}></div>
                                    <span className="text-sm text-gray-600">
                                        {job.transporter.available ? 'Available' : 'Busy'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </Card>

                    {/* Tracking Status */}
                    <Card className="p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                            <Settings className="w-5 h-5 mr-2" />
                            Tracking Systems
                        </h3>
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                    <Navigation className="w-4 h-4 mr-2 text-green-500" />
                                    <span className="text-sm">GPS Tracking</span>
                                </div>
                                <Badge variant="success">Active</Badge>
                            </div>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                    <Camera className="w-4 h-4 mr-2 text-purple-500" />
                                    <span className="text-sm">Photo Updates</span>
                                </div>
                                <Badge variant="purple">Enabled</Badge>
                            </div>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                    <Shield className="w-4 h-4 mr-2 text-blue-500" />
                                    <span className="text-sm">Geofencing</span>
                                </div>
                                <Badge variant="blue">Active</Badge>
                            </div>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                    <Bell className="w-4 h-4 mr-2 text-orange-500" />
                                    <span className="text-sm">Real-time Alerts</span>
                                </div>
                                <Badge variant="warning">Monitoring</Badge>
                            </div>

                            <div className="pt-3 border-t text-xs text-gray-500 space-y-1">
                                <div className="flex justify-between">
                                    <span>Provider:</span>
                                    <span>{job.tracking.provider}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Device Status:</span>
                                    <span className="capitalize">{job.tracking.deviceStatus}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Battery Level:</span>
                                    <span>{job.tracking.batteryLevel}%</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Signal Strength:</span>
                                    <span className="capitalize">{job.tracking.signalStrength}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Update Frequency:</span>
                                    <span>{job.tracking.updateFrequency}</span>
                                </div>
                            </div>
                        </div>
                    </Card>

                    {/* Quick Actions */}
                    <Card className="p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
                        <div className="space-y-2">
                            <Button variant="primary" className="w-full">
                                <MessageSquare className="w-4 h-4 mr-2" />
                                Contact Driver
                            </Button>
                            <Button variant="outline" className="w-full">
                                <Camera className="w-4 h-4 mr-2" />
                                Request Photo Update
                            </Button>
                            <Button variant="outline" className="w-full">
                                <AlertTriangle className="w-4 h-4 mr-2" />
                                Report Issue
                            </Button>
                            <Button variant="outline" className="w-full">
                                <Share2 className="w-4 h-4 mr-2" />
                                Share Location
                            </Button>
                        </div>
                    </Card>
                </div>
            </div>
        </div>
    );

    const TrackingTab = () => (
        <div className="space-y-6">
            {/* Live Tracking Header */}
            <Card className="p-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                        <MapPin className="w-5 h-5 mr-2" />
                        Live Vehicle Tracking
                    </h3>
                    <div className="flex items-center space-x-4">
                        <div className="flex items-center text-sm text-gray-600">
                            <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
                            Live Tracking Active
                        </div>
                        <Button variant="outline" size="sm">
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Refresh
                        </Button>
                    </div>
                </div>

                {/* Map */}
                <GoogleMap legs={job.legs} currentLeg={job.route.currentLeg} height="500px" showAllLegs={true} />

                {/* Current Status Cards */}
                <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-blue-700">Current Location</p>
                                <p className="font-medium text-blue-900 truncate">Highway NH48, Maharashtra</p>
                            </div>
                            <Navigation className="w-8 h-8 text-blue-500" />
                        </div>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-green-700">Current Speed</p>
                                <p className="font-medium text-green-900">65 km/h</p>
                            </div>
                            <Activity className="w-8 h-8 text-green-500" />
                        </div>
                    </div>
                    <div className="bg-yellow-50 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-yellow-700">ETA</p>
                                <p className="font-medium text-yellow-900">
                                    {new Date(job.timeline.estimatedDelivery).toLocaleTimeString([], {
                                        hour: '2-digit',
                                        minute: '2-digit'
                                    })}
                                </p>
                            </div>
                            <Clock className="w-8 h-8 text-yellow-500" />
                        </div>
                    </div>
                </div>
            </Card>

            {/* Vehicle Details */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <Truck className="w-5 h-5 mr-2" />
                        Current Vehicle Details
                    </h3>
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="text-sm font-medium text-gray-600">Vehicle Number</label>
                                <p className="text-gray-900 font-medium">{currentLeg?.vehicle.number}</p>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-600">Vehicle Type</label>
                                <p className="text-gray-900">{currentLeg?.vehicle.type}</p>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-600">Capacity</label>
                                <p className="text-gray-900">{currentLeg?.vehicle.capacity}</p>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-600">Year</label>
                                <p className="text-gray-900">{currentLeg?.vehicle.year}</p>
                            </div>
                        </div>

                        <div className="pt-4 border-t">
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-600">Tracking Device</span>
                                <Badge variant="success" size="sm">{currentLeg?.vehicle.trackingDevice}</Badge>
                            </div>
                            <div className="flex items-center justify-between mt-2">
                                <span className="text-sm text-gray-600">Last Maintenance</span>
                                <span className="text-sm text-gray-900">{currentLeg?.vehicle.lastMaintenance}</span>
                            </div>
                        </div>

                        <div className="flex gap-2 pt-2">
                            <Button variant="outline" size="sm" className="flex-1">
                                <Settings className="w-4 h-4 mr-1" />
                                Change Vehicle
                            </Button>
                            <Button variant="outline" size="sm" className="flex-1">
                                <Eye className="w-4 h-4 mr-1" />
                                Vehicle History
                            </Button>
                        </div>
                    </div>
                </Card>

                <Card className="p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <User className="w-5 h-5 mr-2" />
                        Current Driver Details
                    </h3>
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="text-sm font-medium text-gray-600">Driver Name</label>
                                <p className="text-gray-900 font-medium">{currentLeg?.driver.name}</p>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-600">License Number</label>
                                <p className="text-gray-900">{currentLeg?.driver.license}</p>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-600">Experience</label>
                                <p className="text-gray-900">{currentLeg?.driver.experience}</p>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-600">Rating</label>
                                <div className="flex items-center">
                                    <Star className="w-4 h-4 text-yellow-400 mr-1" />
                                    <span className="text-gray-900">{currentLeg?.driver.rating}</span>
                                </div>
                            </div>
                        </div>

                        <div className="pt-4 border-t">
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-600">Current Status</span>
                                <Badge variant={currentLeg?.driver.status === 'on_trip' ? 'warning' : 'success'} size="sm">
                                    {currentLeg?.driver.status.replace('_', ' ')}
                                </Badge>
                            </div>
                            <div className="flex items-center justify-between mt-2">
                                <span className="text-sm text-gray-600">Current Location</span>
                                <span className="text-sm text-gray-900">{currentLeg?.driver.currentLocation}</span>
                            </div>
                        </div>

                        <div className="flex gap-2 pt-2">
                            <Button variant="primary" size="sm" className="flex-1">
                                <Phone className="w-4 h-4 mr-1" />
                                Call Driver
                            </Button>
                            <Button variant="outline" size="sm" className="flex-1">
                                <MessageSquare className="w-4 h-4 mr-1" />
                                Message
                            </Button>
                        </div>
                    </div>
                </Card>
            </div>

            {/* Route Performance */}
            <Card className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Route Performance Metrics</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="text-center">
                        <div className="text-2xl font-bold text-blue-600">{job.route.efficiency}%</div>
                        <div className="text-sm text-gray-600">Route Efficiency</div>
                    </div>
                    <div className="text-center">
                        <div className="text-2xl font-bold text-green-600">{job.route.fuelConsumption}L</div>
                        <div className="text-sm text-gray-600">Fuel Consumed</div>
                    </div>
                    <div className="text-center">
                        <div className="text-2xl font-bold text-yellow-600">₹{job.route.tollCharges}</div>
                        <div className="text-sm text-gray-600">Toll Charges</div>
                    </div>
                    <div className="text-center">
                        <div className="text-2xl font-bold text-purple-600">{job.route.carbonFootprint}kg</div>
                        <div className="text-sm text-gray-600">CO₂ Emissions</div>
                    </div>
                </div>
            </Card>
        </div>
    );

    const LegsTab = () => (
        <div className="space-y-6">
            {/* Legs Overview */}
            <Card className="p-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                        <Map className="w-5 h-5 mr-2" />
                        Route Legs Management ({job.legs.length} legs)
                    </h3>
                    <Button variant="primary" size="sm">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Leg
                    </Button>
                </div>

                <div className="space-y-6">
                    {job.legs.map((leg, index) => (
                        <div key={leg.id} className="border border-gray-200 rounded-lg overflow-hidden">
                            {/* Leg Header */}
                            <div className="p-4 bg-gray-50 border-b">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center">
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${leg.status === 'completed' ? 'bg-green-100 text-green-600' :
                                            leg.status === 'in_transit' ? 'bg-blue-100 text-blue-600' :
                                                'bg-gray-100 text-gray-600'
                                            }`}>
                                            {leg.status === 'completed' ? (
                                                <CheckCircle className="w-4 h-4" />
                                            ) : leg.status === 'in_transit' ? (
                                                <Navigation className="w-4 h-4" />
                                            ) : (
                                                <span className="text-sm font-medium">{index + 1}</span>
                                            )}
                                        </div>
                                        <div>
                                            <h4 className="font-medium text-gray-900">
                                                Leg {index + 1}: {leg.type.charAt(0).toUpperCase() + leg.type.slice(1)}
                                            </h4>
                                            <p className="text-sm text-gray-600">{leg.origin.split(',')[0]} → {leg.destination.split(',')[0]}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-3">
                                        <Badge variant={
                                            leg.status === 'completed' ? 'success' :
                                                leg.status === 'in_transit' ? 'warning' : 'default'
                                        }>
                                            {leg.status.replace('_', ' ')}
                                        </Badge>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => setSelectedLeg(selectedLeg === leg.id ? null : leg.id)}
                                        >
                                            <ChevronRight className={`w-4 h-4 transition-transform ${selectedLeg === leg.id ? 'rotate-90' : ''}`} />
                                        </Button>
                                    </div>
                                </div>
                            </div>

                            {/* Horizontal Progress Tracker */}
                            <div className="p-4">
                                <HorizontalTracker milestones={leg.milestones} legNumber={index + 1} />
                            </div>

                            {/* Expanded Leg Details */}
                            {selectedLeg === leg.id && (
                                <div className="p-6 border-t bg-white">
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                        {/* Left Column - Leg Information */}
                                        <div className="space-y-6">
                                            <div>
                                                <h5 className="font-medium text-gray-900 mb-3">Leg Information</h5>
                                                <div className="grid grid-cols-2 gap-4">
                                                    <EditableField
                                                        label="Leg Number"
                                                        value={leg.legNumber}
                                                        field="legNumber"
                                                        onSave={(field, value) => handleLegFieldSave(leg.id, field, value)}
                                                    />
                                                    <EditableField
                                                        label="Distance"
                                                        value={`${leg.distance} km`}
                                                        field="distance"
                                                        onSave={(field, value) => handleLegFieldSave(leg.id, field, value)}
                                                    />
                                                    <EditableField
                                                        label="Estimated Duration"
                                                        value={`${leg.estimatedHours} hours`}
                                                        field="estimatedHours"
                                                        onSave={(field, value) => handleLegFieldSave(leg.id, field, value)}
                                                    />
                                                    <EditableField
                                                        label="Progress"
                                                        value={`${leg.progress}%`}
                                                        field="progress"
                                                        onSave={(field, value) => handleLegFieldSave(leg.id, field, value)}
                                                    />
                                                </div>
                                            </div>

                                            <div>
                                                <h5 className="font-medium text-gray-900 mb-3">Vehicle & Driver</h5>
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="text-sm font-medium text-gray-600">Vehicle</label>
                                                        <div className="flex items-center justify-between mt-1">
                                                            <div>
                                                                <p className="text-gray-900 font-medium">{leg.vehicle.number}</p>
                                                                <p className="text-sm text-gray-500">{leg.vehicle.type}</p>
                                                            </div>
                                                            <Button variant="outline" size="sm">
                                                                <Edit className="w-4 h-4" />
                                                            </Button>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <label className="text-sm font-medium text-gray-600">Driver</label>
                                                        <div className="flex items-center justify-between mt-1">
                                                            <div>
                                                                <p className="text-gray-900 font-medium">{leg.driver.name}</p>
                                                                <p className="text-sm text-gray-500">{leg.driver.phone}</p>
                                                            </div>
                                                            <Button variant="outline" size="sm">
                                                                <Edit className="w-4 h-4" />
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div>
                                                <h5 className="font-medium text-gray-900 mb-3">Route Details</h5>
                                                <div className="space-y-3">
                                                    <div>
                                                        <label className="text-sm font-medium text-gray-600">Origin</label>
                                                        <p className="text-gray-900">{leg.origin}</p>
                                                    </div>
                                                    <div>
                                                        <label className="text-sm font-medium text-gray-600">Destination</label>
                                                        <p className="text-gray-900">{leg.destination}</p>
                                                    </div>
                                                </div>
                                            </div>

                                            <div>
                                                <h5 className="font-medium text-gray-900 mb-3">Tracking Options</h5>
                                                <div className="space-y-2">
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-sm text-gray-600">GPS Tracking</span>
                                                        <Badge variant={leg.gpsTracking ? 'success' : 'danger'}>
                                                            {leg.gpsTracking ? 'Enabled' : 'Disabled'}
                                                        </Badge>
                                                    </div>
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-sm text-gray-600">Photo Updates</span>
                                                        <Badge variant={leg.photoUpdates ? 'success' : 'danger'}>
                                                            {leg.photoUpdates ? 'Enabled' : 'Disabled'}
                                                        </Badge>
                                                    </div>
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-sm text-gray-600">Geofencing</span>
                                                        <Badge variant={leg.geofencing ? 'success' : 'danger'}>
                                                            {leg.geofencing ? 'Enabled' : 'Disabled'}
                                                        </Badge>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Right Column - Map and Actions */}
                                        <div className="space-y-6">
                                            <div>
                                                <h5 className="font-medium text-gray-900 mb-3">Leg Route Map</h5>
                                                <GoogleMap
                                                    legs={[leg]}
                                                    currentLeg={1}
                                                    height="300px"
                                                    showAllLegs={false}
                                                />
                                            </div>

                                            <div>
                                                <h5 className="font-medium text-gray-900 mb-3">Quick Actions</h5>
                                                <div className="grid grid-cols-2 gap-2">
                                                    <Button variant="outline" size="sm">
                                                        <Phone className="w-4 h-4 mr-1" />
                                                        Call Driver
                                                    </Button>
                                                    <Button variant="outline" size="sm">
                                                        <MessageSquare className="w-4 h-4 mr-1" />
                                                        Message
                                                    </Button>
                                                    <Button variant="outline" size="sm">
                                                        <Camera className="w-4 h-4 mr-1" />
                                                        Photo Update
                                                    </Button>
                                                    <Button variant="outline" size="sm">
                                                        <MapPin className="w-4 h-4 mr-1" />
                                                        Track Live
                                                    </Button>
                                                    <Button variant="outline" size="sm">
                                                        <AlertTriangle className="w-4 h-4 mr-1" />
                                                        Report Issue
                                                    </Button>
                                                    <Button variant="outline" size="sm">
                                                        <Settings className="w-4 h-4 mr-1" />
                                                        Leg Settings
                                                    </Button>
                                                </div>
                                            </div>

                                            {leg.specialInstructions && (
                                                <div>
                                                    <h5 className="font-medium text-gray-900 mb-3">Special Instructions</h5>
                                                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                                                        <p className="text-yellow-800 text-sm">{leg.specialInstructions}</p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </Card>
        </div>
    );

    const TimelineTab = () => (
        <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                <Activity className="w-5 h-5 mr-2" />
                Job Progress Timeline
            </h3>

            {/* Overall Job Progress */}
            <div className="mb-8 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-gray-900">Overall Job Progress</h4>
                    <Badge variant="blue">{job.overallProgress}% Complete</Badge>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                        className="bg-gradient-to-r from-blue-500 to-purple-500 h-3 rounded-full transition-all duration-1000"
                        style={{ width: `${job.overallProgress}%` }}
                    />
                </div>
                <div className="flex justify-between text-sm text-gray-600 mt-2">
                    <span>Started: {new Date(job.timeline.pickupDate).toLocaleDateString()}</span>
                    <span>ETA: {new Date(job.timeline.estimatedDelivery).toLocaleDateString()}</span>
                </div>
            </div>

            {/* Detailed Timeline per Leg */}
            <div className="space-y-8">
                {job.legs.map((leg, legIndex) => (
                    <div key={leg.id} className="border border-gray-200 rounded-lg p-6">
                        <div className="flex items-center justify-between mb-6">
                            <h4 className="font-semibold text-gray-900">
                                Leg {legIndex + 1}: {leg.origin.split(',')[0]} → {leg.destination.split(',')[0]}
                            </h4>
                            <Badge variant={
                                leg.status === 'completed' ? 'success' :
                                    leg.status === 'in_transit' ? 'warning' : 'default'
                            }>
                                {leg.status.replace('_', ' ')}
                            </Badge>
                        </div>

                        {/* Horizontal Progress Tracker for Timeline */}
                        <HorizontalTracker milestones={leg.milestones} legNumber={legIndex + 1} />

                        {/* Detailed Milestone Information */}
                        <div className="mt-6">
                            <h5 className="font-medium text-gray-900 mb-4">Milestone Details</h5>
                            <div className="space-y-3">
                                {leg.milestones.map((milestone, index) => (
                                    <div key={milestone.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                        <div className="flex items-center">
                                            <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${milestone.status === 'completed' ? 'bg-green-100 text-green-600' :
                                                milestone.status === 'in_progress' ? 'bg-blue-100 text-blue-600' :
                                                    'bg-gray-100 text-gray-600'
                                                }`}>
                                                {milestone.status === 'completed' ? (
                                                    <CheckCircle className="w-4 h-4" />
                                                ) : milestone.status === 'in_progress' ? (
                                                    <Radio className="w-4 h-4" />
                                                ) : (
                                                    <span className="text-xs font-bold">{index + 1}</span>
                                                )}
                                            </div>
                                            <div>
                                                <p className="font-medium text-gray-900">{milestone.name}</p>
                                                <p className="text-sm text-gray-600">{milestone.notes}</p>
                                                {milestone.location && (
                                                    <p className="text-xs text-blue-600">{milestone.location}</p>
                                                )}
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            {milestone.timestamp && (
                                                <p className="text-sm text-gray-900">
                                                    {new Date(milestone.timestamp).toLocaleString()}
                                                </p>
                                            )}
                                            <Badge variant={
                                                milestone.status === 'completed' ? 'success' :
                                                    milestone.status === 'in_progress' ? 'warning' : 'default'
                                            } size="sm">
                                                {milestone.status.replace('_', ' ')}
                                            </Badge>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </Card>
    );

    const AlertsTab = () => (
        <div className="space-y-6">
            {/* Alerts Overview */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="p-4">
                    <div className="flex items-center">
                        <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                            <AlertTriangle className="w-5 h-5 text-red-600" />
                        </div>
                        <div className="ml-3">
                            <p className="text-xs text-gray-600">Active Alerts</p>
                            <p className="text-lg font-bold text-gray-900">{alerts.filter(a => !a.resolved).length}</p>
                        </div>
                    </div>
                </Card>
                <Card className="p-4">
                    <div className="flex items-center">
                        <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                            <AlertCircle className="w-5 h-5 text-yellow-600" />
                        </div>
                        <div className="ml-3">
                            <p className="text-xs text-gray-600">High Priority</p>
                            <p className="text-lg font-bold text-gray-900">{alerts.filter(a => a.severity === 'high').length}</p>
                        </div>
                    </div>
                </Card>
                <Card className="p-4">
                    <div className="flex items-center">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                            <Info className="w-5 h-5 text-blue-600" />
                        </div>
                        <div className="ml-3">
                            <p className="text-xs text-gray-600">Notifications</p>
                            <p className="text-lg font-bold text-gray-900">{alerts.filter(a => a.type === 'info').length}</p>
                        </div>
                    </div>
                </Card>
                <Card className="p-4">
                    <div className="flex items-center">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                            <CheckCircle className="w-5 h-5 text-green-600" />
                        </div>
                        <div className="ml-3">
                            <p className="text-xs text-gray-600">Resolved</p>
                            <p className="text-lg font-bold text-gray-900">{alerts.filter(a => a.resolved).length}</p>
                        </div>
                    </div>
                </Card>
            </div>

            {/* Alert List */}
            <Card className="p-6">
                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                        <Bell className="w-5 h-5 mr-2" />
                        Real-time Alerts & Monitoring
                    </h3>
                    <div className="flex items-center space-x-2">
                        <Button variant="outline" size="sm">
                            <Settings className="w-4 h-4 mr-2" />
                            Alert Settings
                        </Button>
                        <Button variant="primary" size="sm">
                            <Plus className="w-4 h-4 mr-2" />
                            Create Alert
                        </Button>
                    </div>
                </div>

                <div className="space-y-4">
                    {alerts.map((alert) => (
                        <AlertCard
                            key={alert.id}
                            alert={alert}
                            onResolve={handleAlertResolve}
                            onDismiss={handleAlertDismiss}
                        />
                    ))}
                </div>

                {alerts.length === 0 && (
                    <div className="text-center py-8">
                        <Bell className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No Active Alerts</h3>
                        <p className="text-gray-600">All systems are running smoothly. You'll be notified here if any issues arise.</p>
                    </div>
                )}
            </Card>

            {/* Monitoring Systems Status */}
            <Card className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Monitoring Systems Status</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="p-4 border border-gray-200 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center">
                                <Navigation className="w-4 h-4 text-green-500 mr-2" />
                                <span className="font-medium">GPS Tracking</span>
                            </div>
                            <Badge variant="success" size="sm">Online</Badge>
                        </div>
                        <p className="text-sm text-gray-600">Last update: 2 minutes ago</p>
                        <p className="text-sm text-gray-600">Signal: Strong (95%)</p>
                    </div>

                    <div className="p-4 border border-gray-200 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center">
                                <Thermometer className="w-4 h-4 text-blue-500 mr-2" />
                                <span className="font-medium">Temperature</span>
                            </div>
                            <Badge variant="success" size="sm">Normal</Badge>
                        </div>
                        <p className="text-sm text-gray-600">Current: {job.container.temperature}°C</p>
                        <p className="text-sm text-gray-600">Range: Optimal</p>
                    </div>

                    <div className="p-4 border border-gray-200 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center">
                                <Shield className="w-4 h-4 text-purple-500 mr-2" />
                                <span className="font-medium">Geofencing</span>
                            </div>
                            <Badge variant="success" size="sm">Active</Badge>
                        </div>
                        <p className="text-sm text-gray-600">5 zones configured</p>
                        <p className="text-sm text-gray-600">No violations</p>
                    </div>

                    <div className="p-4 border border-gray-200 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center">
                                <Camera className="w-4 h-4 text-purple-500 mr-2" />
                                <span className="font-medium">Photo Updates</span>
                            </div>
                            <Badge variant="success" size="sm">Enabled</Badge>
                        </div>
                        <p className="text-sm text-gray-600">Last photo: 15 min ago</p>
                        <p className="text-sm text-gray-600">Auto-capture: ON</p>
                    </div>

                    <div className="p-4 border border-gray-200 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center">
                                <Bell className="w-4 h-4 text-orange-500 mr-2" />
                                <span className="font-medium">Alert System</span>
                            </div>
                            <Badge variant="success" size="sm">Active</Badge>
                        </div>
                        <p className="text-sm text-gray-600">Real-time monitoring</p>
                        <p className="text-sm text-gray-600">SMS & Email alerts</p>
                    </div>

                    <div className="p-4 border border-gray-200 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center">
                                <Activity className="w-4 h-4 text-green-500 mr-2" />
                                <span className="font-medium">Device Health</span>
                            </div>
                            <Badge variant="success" size="sm">Good</Badge>
                        </div>
                        <p className="text-sm text-gray-600">Battery: {job.tracking.batteryLevel}%</p>
                        <p className="text-sm text-gray-600">No issues detected</p>
                    </div>
                </div>
            </Card>
        </div>
    );

    const DocumentsTab = () => (
        <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <FileText className="w-5 h-5 mr-2" />
                    Job Documents & Compliance
                </h3>
                <Button variant="primary" size="sm">
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Document
                </Button>
            </div>

            {/* Document Categories */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-gray-900">Required Documents</h4>
                        <Badge variant="warning" size="sm">2 Pending</Badge>
                    </div>
                    <p className="text-sm text-gray-600">Critical documents needed for job completion</p>
                </div>
                <div className="p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-gray-900">Compliance Docs</h4>
                        <Badge variant="success" size="sm">Complete</Badge>
                    </div>
                    <p className="text-sm text-gray-600">All regulatory requirements satisfied</p>
                </div>
                <div className="p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-gray-900">Photo Evidence</h4>
                        <Badge variant="blue" size="sm">12 Photos</Badge>
                    </div>
                    <p className="text-sm text-gray-600">Real-time photos from vehicle</p>
                </div>
            </div>

            {/* Document List */}
            <div className="space-y-4">
                {[
                    { name: 'Bill of Lading', status: 'uploaded', type: 'required', uploadedAt: '2025-06-05T08:30:00', size: '2.4 MB' },
                    { name: 'Commercial Invoice', status: 'uploaded', type: 'required', uploadedAt: '2025-06-05T08:45:00', size: '1.8 MB' },
                    { name: 'Customs Declaration', status: 'uploaded', type: 'compliance', uploadedAt: '2025-06-05T09:00:00', size: '0.9 MB' },
                    { name: 'Proof of Delivery', status: 'pending', type: 'required', uploadedAt: null, size: null },
                    { name: 'Final Delivery Receipt', status: 'pending', type: 'required', uploadedAt: null, size: null },
                    { name: 'Vehicle Photos - Loading', status: 'uploaded', type: 'photo', uploadedAt: '2025-06-05T10:30:00', size: '5.2 MB' },
                    { name: 'Vehicle Photos - Transit', status: 'uploaded', type: 'photo', uploadedAt: '2025-06-07T10:30:00', size: '3.1 MB' }
                ].map((doc, index) => (
                    <div key={index} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
                        <div className="flex items-center">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-3 ${doc.type === 'required' ? 'bg-blue-100' :
                                doc.type === 'compliance' ? 'bg-green-100' : 'bg-purple-100'
                                }`}>
                                {doc.type === 'photo' ? (
                                    <Camera className={`w-5 h-5 ${doc.type === 'required' ? 'text-blue-600' :
                                        doc.type === 'compliance' ? 'text-green-600' : 'text-purple-600'
                                        }`} />
                                ) : (
                                    <FileText className={`w-5 h-5 ${doc.type === 'required' ? 'text-blue-600' :
                                        doc.type === 'compliance' ? 'text-green-600' : 'text-purple-600'
                                        }`} />
                                )}
                            </div>
                            <div>
                                <h4 className="font-medium text-gray-900">{doc.name}</h4>
                                <div className="flex items-center space-x-2 text-sm text-gray-500">
                                    {doc.uploadedAt && (
                                        <span>Uploaded: {new Date(doc.uploadedAt).toLocaleString()}</span>
                                    )}
                                    {doc.size && (
                                        <>
                                            <span>•</span>
                                            <span>{doc.size}</span>
                                        </>
                                    )}
                                    <span>•</span>
                                    <span className="capitalize">{doc.type}</span>
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <Badge variant={doc.status === 'uploaded' ? 'success' : 'warning'}>
                                {doc.status}
                            </Badge>
                            {doc.status === 'uploaded' && (
                                <>
                                    <Button variant="outline" size="sm">
                                        <Eye className="w-4 h-4" />
                                    </Button>
                                    <Button variant="outline" size="sm">
                                        <Download className="w-4 h-4" />
                                    </Button>
                                </>
                            )}
                            {doc.status === 'pending' && (
                                <Button variant="primary" size="sm">
                                    <Upload className="w-4 h-4 mr-1" />
                                    Upload
                                </Button>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/* Document Upload Portal */}
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center">
                    <ExternalLink className="w-5 h-5 text-blue-600 mr-2" />
                    <div>
                        <p className="font-medium text-blue-900">Shared Document Portal</p>
                        <p className="text-sm text-blue-600">Access collaborative document workspace for all stakeholders</p>
                    </div>
                    <Button variant="outline" size="sm" className="ml-auto">
                        Open Portal
                    </Button>
                </div>
            </div>
        </Card>
    );

    return (
        <div className="min-h-screen bg-gray-50 p-6 space-y-6">
            {/* Enhanced Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center">
                    <Button variant="ghost" onClick={() => window.history.back()} className="mr-4">
                        <ArrowLeft className="w-4 h-4" />
                    </Button>
                    <div>
                        <div className="flex items-center gap-3 mb-2">
                            <h1 className="text-3xl font-bold text-gray-900">{job.jobNumber}</h1>
                            <Badge variant={job.status === 'completed' ? 'success' : job.status === 'in_transit' ? 'warning' : 'blue'}>
                                {job.status.replace('_', ' ').toUpperCase()}
                            </Badge>
                            <Badge variant={job.priority === 'high' ? 'danger' : job.priority === 'medium' ? 'warning' : 'default'}>
                                {job.priority.toUpperCase()} PRIORITY
                            </Badge>
                            <div className="flex items-center">
                                <Star className="w-4 h-4 text-yellow-400 mr-1" />
                                <span className="text-sm text-gray-600">4.5</span>
                            </div>
                        </div>
                        <p className="text-gray-600 font-medium">
                            {job.legs[0]?.origin.split(',')[0]} → {job.legs[job.legs.length - 1]?.destination.split(',')[0]} • {job.container.number}
                        </p>
                        <div className="flex items-center mt-1 text-sm text-gray-500 space-x-4">
                            <span className="flex items-center">
                                <Hash className="w-3 h-3 mr-1" />
                                {job.customerPO}
                            </span>
                            <span className="flex items-center">
                                <Map className="w-3 h-3 mr-1" />
                                {job.route.totalDistance}km • {job.legs.length} legs
                            </span>
                            <span className="flex items-center">
                                <Truck className="w-3 h-3 mr-1" />
                                {job.transporter.name} ({job.transporter.code})
                            </span>
                            <span className="flex items-center">
                                <Timer className="w-3 h-3 mr-1" />
                                {job.route.estimatedDuration}h estimated
                            </span>
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <Button variant="outline" size="sm">
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Refresh
                    </Button>
                    <Button variant="outline" size="sm">
                        <Share2 className="w-4 h-4 mr-2" />
                        Share
                    </Button>
                    <Button variant="outline" size="sm">
                        <Download className="w-4 h-4 mr-2" />
                        Export
                    </Button>
                    <Button variant="secondary" size="sm">
                        <MessageSquare className="w-4 h-4 mr-2" />
                        Contact All
                    </Button>
                </div>
            </div>

            {/* Real-time Alerts */}
            {alerts.filter(a => !a.resolved).length > 0 && (
                <div className="space-y-2">
                    {alerts.filter(a => !a.resolved).slice(0, 2).map((alert) => (
                        <AlertCard
                            key={alert.id}
                            alert={alert}
                            onResolve={handleAlertResolve}
                            onDismiss={handleAlertDismiss}
                        />
                    ))}
                </div>
            )}

            {/* Overall Progress Bar */}
            <Card className="p-6">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold text-gray-900">Overall Job Progress</h3>
                    <div className="flex items-center space-x-4">
                        <Badge variant="blue" size="sm">{job.overallProgress}% Complete</Badge>
                        <span className="text-sm text-gray-600">Leg {job.route.currentLeg} of {job.legs.length}</span>
                    </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-4">
                    <div
                        className="bg-gradient-to-r from-blue-500 to-green-500 h-4 rounded-full transition-all duration-1000 flex items-center justify-end pr-2"
                        style={{ width: `${job.overallProgress}%` }}
                    >
                        <Truck className="w-3 h-3 text-white" />
                    </div>
                </div>
                <div className="flex justify-between text-sm text-gray-600 mt-2">
                    <span>Started: {new Date(job.timeline.pickupDate).toLocaleDateString()}</span>
                    <span>Current: {currentLeg?.vehicle.number} • {currentLeg?.driver.name}</span>
                    <span>ETA: {new Date(job.timeline.estimatedDelivery).toLocaleDateString()}</span>
                </div>
            </Card>

            {/* Tab Navigation */}
            <Card>
                <div className="border-b border-gray-200">
                    <nav className="flex space-x-8 px-6">
                        {tabs.map((tab) => {
                            const TabIcon = tab.icon;
                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === tab.id
                                        ? 'border-blue-500 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                        }`}
                                >
                                    <div className="flex items-center">
                                        <TabIcon className="w-4 h-4 mr-2" />
                                        {tab.label}
                                        {tab.id === 'alerts' && alerts.filter(a => !a.resolved).length > 0 && (
                                            <Badge variant="danger" size="sm" className="ml-2">
                                                {alerts.filter(a => !a.resolved).length}
                                            </Badge>
                                        )}
                                    </div>
                                </button>
                            );
                        })}
                    </nav>
                </div>

                {/* Tab Content */}
                <div className="p-6">
                    {activeTab === 'overview' && <OverviewTab />}
                    {activeTab === 'tracking' && <TrackingTab />}
                    {activeTab === 'legs' && <LegsTab />}
                    {activeTab === 'timeline' && <TimelineTab />}
                    {activeTab === 'alerts' && <AlertsTab />}
                    {activeTab === 'documents' && <DocumentsTab />}
                </div>
            </Card>
        </div>
    );
};

export default JobDetailsPage;